import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { Bell, Search, MessageSquare, Home } from 'react-feather';
import { useSymbolPrecisions } from '../../hooks/useSymbolPrecisions';
import { Select } from '../common/Select';
import { WalletButton } from '../wallet/WalletButton';
import ThemeToggleButton from '../common/ThemeToggleButton'; // Import the new component

// Define props interface - Phase 2: Enhanced for symbol sync
interface TopNavbarProps {
  onSymbolChange: (symbol: string) => void;
  onToggleChat?: () => void;
  currentSymbol?: string; // Phase 2: Add current symbol for controlled component
}

// Navbar container
const Nav = styled.nav`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing(1)} ${({ theme }) => theme.spacing(2)};
  background-color: ${({ theme }) => theme.colors.background.secondary};
  border-bottom: 1px solid ${({ theme }) => theme.colors.divider};
  height: 60px;
`;

// Left section of navbar with logo and markets dropdown
const NavLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
`;

// Logo styling
const Logo = styled(Link)`
  font-size: 1.5rem;
  font-weight: bold;
  color: ${({ theme }) => theme.colors.text.primary};
  text-decoration: none;
  
  &:hover {
    color: ${({ theme }) => theme.colors.primary.main};
  }
`;

// Phase 2: Improved symbol selector styling
const SymbolSelectorContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 150px;
`;

const SymbolLabel = styled.span`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const SymbolStatus = styled.span<{ type: 'loading' | 'error' | 'success' }>`
  font-size: 0.8rem;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  color: ${({ type }) => {
    switch (type) {
      case 'loading': return '#3b82f6';
      case 'error': return '#ef4444';
      case 'success': return '#10b981';
      default: return '#6b7280';
    }
  }};
  background-color: ${({ type }) => {
    switch (type) {
      case 'loading': return '#1e40af20';
      case 'error': return '#dc262620';
      case 'success': return '#05965320';
      default: return '#37415120';
    }
  }};
  border: 1px solid ${({ type }) => {
    switch (type) {
      case 'loading': return '#1e40af40';
      case 'error': return '#dc262640';
      case 'success': return '#05965340';
      default: return '#37415140';
    }
  }};
`;

// Center section with search bar
const NavCenter = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  padding: 0 2rem;
`;

// Search bar
const SearchContainer = styled.div`
  position: relative;
  width: 100%;
  max-width: 600px;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: ${({ theme }) => theme.spacing(1)} ${({ theme }) => theme.spacing(3)};
  border-radius: ${({ theme }) => theme.shape.borderRadius.medium};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background-color: ${({ theme }) => theme.colors.background.primary};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 0.9rem;
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary.main};
  }
  
  &::placeholder {
    color: ${({ theme }) => theme.colors.text.secondary};
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: ${({ theme }) => theme.colors.text.secondary};
`;

// Right section with notification and AI icons
const NavRight = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

// Icon button style
const IconButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.text.secondary};
  cursor: pointer;
  padding: ${({ theme }) => theme.spacing(1)};
  border-radius: ${({ theme }) => theme.shape.borderRadius.small};
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.background.paper};
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

const TopNavbar: React.FC<TopNavbarProps> = ({ onSymbolChange, onToggleChat, currentSymbol }) => {
  // Fetch symbols using the hook
  const { symbols, isLoading: loadingSymbols, error: symbolError } = useSymbolPrecisions();
  
  // Phase 2: Local state for symbol search functionality
  const [searchQuery, setSearchQuery] = useState('');

  // Phase 2: Set default symbol when symbols are loaded
  useEffect(() => {
    if (symbols.length > 0 && !currentSymbol) {
      // Look for BTC-PERP first, then fallback to first symbol
      const btcSymbol = symbols.find(s => s.startsWith('BTC'));
      const defaultSymbol = btcSymbol || symbols[0];
      onSymbolChange(defaultSymbol);
      console.log(`TopNavbar: Set default symbol to ${defaultSymbol} from ${symbols.length} available symbols`);
    }
  }, [symbols, currentSymbol, onSymbolChange]);

  const handleSelectChange = (selectedSymbol: string) => {
    onSymbolChange(selectedSymbol);
    console.log(`Symbol changed to: ${selectedSymbol}`); // Phase 2: Debug logging
  };

  // Phase 2: Search functionality for future implementation
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
    // TODO: Implement symbol search/filtering
  };

  // Phase 2: Render symbol selector with improved error handling
  const renderSymbolSelector = () => {
    console.log(`TopNavbar renderSymbolSelector: loading=${loadingSymbols}, error=${symbolError?.message}, symbols.length=${symbols.length}`);
    
    if (loadingSymbols) {
      return (
        <SymbolSelectorContainer>
          <SymbolLabel>Market</SymbolLabel>
          <SymbolStatus type="loading">Loading symbols...</SymbolStatus>
        </SymbolSelectorContainer>
      );
    }

    if (symbolError) {
      console.error('Symbol loading error:', symbolError);
      return (
        <SymbolSelectorContainer>
          <SymbolLabel>Market</SymbolLabel>
          <SymbolStatus type="error">
            Failed to load symbols. Please refresh.
          </SymbolStatus>
        </SymbolSelectorContainer>
      );
    }

    if (symbols.length === 0) {
      return (
        <SymbolSelectorContainer>
          <SymbolLabel>Market</SymbolLabel>
          <SymbolStatus type="error">No symbols available</SymbolStatus>
        </SymbolSelectorContainer>
      );
    }

    return (
      <SymbolSelectorContainer>
        <SymbolLabel>Market</SymbolLabel>
        <Select 
          id="symbol-selector" 
          onChange={handleSelectChange} 
          value={currentSymbol || symbols[0]}
          options={symbols.map(s => ({ value: s, label: s }))}
          style={{ minWidth: '150px' }}
        />
      </SymbolSelectorContainer>
    );
  };

  return (
    <Nav>
      <NavLeft>
        <IconButton aria-label="Home" as={Link} to="/">
          <Home size={20} />
        </IconButton>
        <Logo to="/">CondorV3</Logo>
        
        {/* Phase 2: Improved symbol selector */}
        {renderSymbolSelector()}
      </NavLeft>
      
      <NavCenter>
        <SearchContainer>
          <SearchIcon>
            <Search size={16} />
          </SearchIcon>
          <SearchInput 
            placeholder={`Search symbols... (${symbols.length} available)`}
            value={searchQuery}
            onChange={handleSearchChange}
          />
        </SearchContainer>
      </NavCenter>
      
      <NavRight>
        <ThemeToggleButton />
        <IconButton aria-label="Notifications">
          <Bell size={20} />
        </IconButton>
        <WalletButton />
        <IconButton aria-label="AI Chat" onClick={onToggleChat}>
          <MessageSquare size={20} />
        </IconButton>
      </NavRight>
    </Nav>
  );
};

export default TopNavbar; 